import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress
} from "@mui/material";
import {
  DeleteOutline as DeleteIcon,
  WarningAmber as WarningIcon
} from "@mui/icons-material";

/**
 * Reusable Confirmation Dialog for deleting attached files / prerequisite resources
 */
const DeleteAttachmentConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  fileName = "",
  isDeleting = false,
  title = "Delete Attached File",
  description
}) => {
  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isDeleting && onClose) {
          onClose();
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: "14px",
          p: 1,
          minWidth: { xs: "320px", sm: "420px" },
          boxShadow: "0 10px 30px rgba(0,0,0,0.1)"
        }
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            backgroundColor: "#fef2f2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ef4444"
          }}
        >
          <WarningIcon sx={{ fontSize: 20 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#0f172a" }}>
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <DialogContentText sx={{ fontSize: "0.875rem", color: "#64748b" }}>
          {description ? (
            description
          ) : (
            <>
              Are you sure you want to delete{" "}
              <strong style={{ color: "#1e293b" }}>"{fileName}"</strong>? This
              action will permanently remove the file from storage and cannot be undone.
            </>
          )}
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 0, gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          disabled={isDeleting}
          onClick={onClose}
          sx={{
            borderRadius: "8px",
            borderColor: "#cbd5e1",
            color: "#64748b",
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            "&:hover": { borderColor: "#94a3b8", backgroundColor: "#f8fafc" }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          color="error"
          disabled={isDeleting}
          onClick={onConfirm}
          startIcon={
            isDeleting ? (
              <CircularProgress size={14} sx={{ color: "#ffffff" }} />
            ) : (
              <DeleteIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            borderRadius: "8px",
            backgroundColor: "#ef4444",
            textTransform: "none",
            fontWeight: 700,
            px: 2.2,
            boxShadow: "none",
            "&:hover": { backgroundColor: "#dc2626", boxShadow: "none" }
          }}
        >
          {isDeleting ? "Deleting..." : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteAttachmentConfirmDialog;
